import { InvestorBidStatus, PricingType, Unit } from '~/constants';
import { Project, ProjectBid, ProjectBidSummary } from '~/types/project';
import epcCost from './epcCost';
import getBudgetCostAmount from './getBudgetCostAmount';
import { sizeKwdc } from './system';

export const normalizeToOverall = (sizeKwdc: number, budgetItem: ProjectBid) => {
  if (!budgetItem?.amount) {
    return 0;
  }
  return (budgetItem?.unit === Unit.WDC
      ? budgetItem?.amount * sizeKwdc * 1000
      : budgetItem?.amount
  ) ?? 0;
};

export const normalizeToPerWdc = (sizeKwdc: number, budgetItem: ProjectBid) => {
  if (!budgetItem?.amount) {
    return 0;
  }
  return (budgetItem?.unit === Unit.WDC
      ? budgetItem?.amount
      : budgetItem?.amount / (sizeKwdc * 1000)
  ) ?? 0;
};

export const normalizeToPerKwhStorage = (sizeKwh: number, budgetItem: ProjectBid) => {
  if (!budgetItem?.amount) {
    return 0;
  }
  return (budgetItem?.unit === Unit.KWH
      ? budgetItem?.amount
      : budgetItem?.amount / sizeKwh
  ) ?? 0;
};

export const normalizeToOverallStorage = (sizeKwh: number, budgetItem: ProjectBid) => {
  if (!budgetItem?.amount) {
    return 0;
  }
  return (budgetItem?.unit === Unit.KWH
      ? budgetItem?.amount * sizeKwh
      : budgetItem?.amount
  ) ?? 0;
};

export const getExpectedItc = (project: Project) => {
  // return a sum of itc adders + 30%
  return project?.itcAdders?.reduce((acc: any, adder: any) => acc + adder?.value || 0, 0) + 30;
};

export const getExpectedItcCost = (project: Project, epcCostsTotalFromCosts?: number) => {
  // use epcCostsTotalFromCosts from projectCosts if available 
  // this is needed to calculate ITC percentage fee on the sell side
  // epcCost function only works for investors or projects that have epcCost set, 
  // and is also used to populate the Excel file

  const expectedItc = getExpectedItc(project);
  const epcCostTotalFromFn = epcCost(project) ?? 0;
  const epcCostTotal = epcCostsTotalFromCosts ?? epcCostTotalFromFn;

  return (

    epcCostTotal + 
    (getBudgetCostAmount(project, 'INTERCONNECTION') ?? 0) +
    (getBudgetCostAmount(project, 'OTHER_ITC') ?? 0)
  ) 
  * (expectedItc / 100);
};


export const getBidData = (project: Project, permissions: any, bid?: ProjectBid) => {

  // console.log('costsFns@getBidData bid', bid);
  //returns one investor bid
  const buySideBidData = project?.projectBids?.find((bid: ProjectBid) => bid.bidStatus === InvestorBidStatus.ACCEPTED || bid.bidStatus === InvestorBidStatus.PENDING);
  //returns all bids for the project with seller fees
  const sellSideBidStatus = project?.bidSummary?.filter((row: ProjectBidSummary) => {
    if (row?.bid) {
      return row.bid.bidStatus === InvestorBidStatus.ACCEPTED || row.bid.bidStatus === InvestorBidStatus.PENDING;
    } else {
      return null;
    }
  });
  const sellSideBidData = sellSideBidStatus?.map((row: ProjectBidSummary) => row.bid) ?? [];

  // console.log('costsFns@getBidData buySideBidData', buySideBidData);
  // console.log('costsFns@getBidData sellSideBidData', sellSideBidData);

  // if the project has existing bid data and an investor bids again, return the bid
  if (bid?.amount) {
    return bid;
  }
  if (buySideBidData && permissions.hasProjectInvestorAccess && !permissions.isAdmin) { 
    return buySideBidData;
  } 
  if (Array.isArray(sellSideBidData) && sellSideBidData.length > 0 && permissions.hasProjectDeveloperAccess) {
    return sellSideBidData; 
  }
  
  return null;
};

// Given a project and a bid, get the bid amount converted to total $
export const getProjectBidOverallAmount = (
  project: Project, 
  bid: ProjectBid, 
) => {
  if (!project || !bid?.amount) return;
  let amount: number = 0;
  if (project?.pricingType === PricingType.ITC && bid.unit === Unit.PERCENT) {
    const itcCost = getExpectedItcCost(project);
    amount = itcCost * bid.amount / 100;
  } else {
    amount = normalizeToOverall(sizeKwdc(project), bid);
  }

  return amount;
};


